/* ==========================================================================
   Moksha Wired — theme.js
   Vanilla JS, no dependencies. Progressive enhancement: every feature has a
   working no-JS fallback in the Liquid (cart page, filter links, forms, details).
   ========================================================================== */
(function () {
  'use strict';

  var T = window.mokshaTheme || { routes: {}, settings: {}, strings: {} };
  var S = T.strings || {};
  var R = T.routes || {};

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  /* ---------- helpers ---------- */
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }
  function store(key, val) {
    try {
      if (val === undefined) return window.localStorage.getItem(key);
      window.localStorage.setItem(key, val);
    } catch (e) { /* storage unavailable */ }
    return null;
  }
  function announce(msg) {
    var region = $('[data-live-region]');
    if (!region || !msg) return;
    region.textContent = '';
    setTimeout(function () { region.textContent = msg; }, 60);
  }
  function parseHTML(html) { return new DOMParser().parseFromString(html, 'text/html'); }
  function prefersReducedMotion() { return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
  function mq(q) { return window.matchMedia(q); }
  function isDesktopFilters() { return mq('(min-width: 990px)').matches; }

  /* ======================================================================
     Drawers (menu, cart, search, mobile filters)
     ====================================================================== */
  var Drawers = {
    active: null,
    trigger: null,

    overlay: function () { return $('[data-overlay]'); },

    init: function (root) {
      // The sticky header creates a stacking context, so the menu drawer is moved to <body>.
      $$('[data-drawer="menu"]', root).forEach(function (drawer) {
        if (drawer.parentElement === document.body) return;
        $$('body > [data-drawer="menu"]').forEach(function (old) { if (old !== drawer) old.remove(); });
        document.body.appendChild(drawer);
      });
      this.syncFilters();
    },

    syncFilters: function () {
      var filters = $('[data-drawer="filters"]');
      if (!filters) return;
      if (isDesktopFilters()) {
        filters.removeAttribute('inert');
        filters.classList.remove('is-open');
        if (this.active === filters) this.close(true);
      } else if (!filters.classList.contains('is-open')) {
        filters.setAttribute('inert', '');
      }
    },

    open: function (name, trigger) {
      var drawer = $('[data-drawer="' + name + '"]');
      if (!drawer) return false;
      if (name === 'filters' && isDesktopFilters()) return false;
      if (this.active && this.active !== drawer) this.close(true);
      this.active = drawer;
      this.trigger = trigger || document.activeElement;
      drawer.removeAttribute('inert');
      drawer.setAttribute('role', 'dialog');
      drawer.setAttribute('aria-modal', 'true');
      // force a frame so the transition runs
      void drawer.offsetWidth;
      drawer.classList.add('is-open');
      var ov = this.overlay();
      if (ov) ov.classList.add('is-active');
      document.body.classList.add('has-drawer');
      $$('[data-drawer-open="' + name + '"], [data-cart-open]').forEach(function (b) {
        if (name === 'cart' || b.getAttribute('data-drawer-open') === name) b.setAttribute('aria-expanded', 'true');
      });
      var first = $('[data-drawer-close]', drawer) || $(FOCUSABLE, drawer);
      var heading = $('.drawer__title, .filters__title', drawer);
      setTimeout(function () { (first || heading || drawer).focus({ preventScroll: true }); }, 50);
      return true;
    },

    close: function (skipFocus) {
      var drawer = this.active;
      if (!drawer) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('inert', '');
      var ov = this.overlay();
      if (ov) ov.classList.remove('is-active');
      document.body.classList.remove('has-drawer');
      $$('[aria-expanded="true"][data-drawer-open], [aria-expanded="true"][data-cart-open]').forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      var back = this.trigger;
      this.active = null;
      this.trigger = null;
      if (!skipFocus && back && document.contains(back) && back.focus) back.focus();
    },

    trap: function (e) {
      if (e.key !== 'Tab' || !this.active) return;
      var items = $$(FOCUSABLE, this.active).filter(function (el) { return el.offsetParent !== null || el === document.activeElement; });
      if (!items.length) return;
      var first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  };

  /* ======================================================================
     Cart
     ====================================================================== */
  var Cart = {
    queue: Promise.resolve(),

    drawerEnabled: function () { return !!$('[data-drawer="cart"]') && T.settings.cartType === 'drawer'; },

    get: function () {
      return fetch(R.cartJs, { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
    },

    setCount: function (n) {
      $$('[data-cart-count]').forEach(function (el) {
        el.textContent = n;
        el.setAttribute('data-count', n);
        el.removeAttribute('aria-label');
      });
      $$('[data-cart-title-count]').forEach(function (el) { el.textContent = '(' + n + ')'; });
    },

    sectionIds: function () {
      var ids = [];
      if ($('[data-cart-drawer-inner]')) ids.push('cart-drawer');
      var page = $('[data-cart-page]');
      if (page && page.getAttribute('data-section-id')) ids.push(page.getAttribute('data-section-id'));
      return ids;
    },

    render: function (sections) {
      if (!sections) return;
      var drawerHTML = sections['cart-drawer'];
      if (drawerHTML) {
        var doc = parseHTML(drawerHTML);
        var next = $('[data-cart-drawer-inner]', doc);
        var cur = $('[data-cart-drawer-inner]');
        if (next && cur) cur.replaceWith(next);
      }
      var page = $('[data-cart-page]');
      if (page) {
        var pid = page.getAttribute('data-section-id');
        if (pid && sections[pid]) {
          var pdoc = parseHTML(sections[pid]);
          var pnext = $('[data-cart-page]', pdoc);
          if (pnext) page.replaceWith(pnext);
        }
      }
    },

    refresh: function () {
      var ids = this.sectionIds();
      var self = this;
      var url = (R.cart || '/cart') + '?sections=' + encodeURIComponent(ids.join(','));
      return fetch(url, { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (sections) { self.render(sections); return self.get(); })
        .then(function (cart) { self.setCount(cart.item_count); return cart; });
    },

    change: function (line, quantity, focusInfo) {
      var self = this;
      var body = JSON.stringify({
        line: line,
        quantity: quantity,
        sections: this.sectionIds().join(','),
        sections_url: window.location.pathname
      });
      this.queue = this.queue.then(function () {
        $$('.lines').forEach(function (l) { l.classList.add('is-updating'); });
        return fetch(R.cartChange, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: body })
          .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, data: j }; }); })
          .then(function (res) {
            if (!res.ok || res.data.status || res.data.errors) {
              self.lineError(line, res.data.message || res.data.description || S.cartError);
              return;
            }
            self.render(res.data.sections);
            self.setCount(res.data.item_count);
            announce(quantity === 0 ? S.itemRemoved : S.cartUpdated);
            self.restoreFocus(focusInfo);
          })
          .catch(function () { self.lineError(line, S.cartError); })
          .then(function () { $$('.lines').forEach(function (l) { l.classList.remove('is-updating'); }); });
      });
      return this.queue;
    },

    lineError: function (line, msg) {
      var li = $('[data-line-item="' + line + '"]');
      var el = li && $('[data-line-error]', li);
      if (el) {
        el.textContent = msg;
        el.hidden = false;
        var input = $('.qty__input', li);
        if (input && input.getAttribute('data-prev')) input.value = input.getAttribute('data-prev');
      } else {
        var g = $('[data-cart-error]');
        if (g) { g.textContent = msg; g.hidden = false; }
      }
      announce(msg);
    },

    restoreFocus: function (info) {
      if (!info) return;
      var target = null;
      var li = $('[data-line-item="' + info.line + '"]');
      if (li) {
        if (info.kind === 'plus') target = $('[data-qty-plus]', li);
        else if (info.kind === 'minus') target = $('[data-qty-minus]', li);
        else if (info.kind === 'input') target = $('.qty__input', li);
      }
      if (!target) {
        target = $('[data-remove-line]') || $('[data-cart-drawer-inner] a.btn') || $('[data-cart-page] a.btn');
      }
      if (target) target.focus({ preventScroll: true });
    },

    add: function (form, btn) {
      var self = this;
      var fd = new FormData(form);
      var useDrawer = this.drawerEnabled();
      if (useDrawer) {
        fd.append('sections', 'cart-drawer');
        fd.append('sections_url', window.location.pathname);
      }
      var label = $('[data-add-label]', btn);
      var origLabel = label ? label.textContent : '';
      btn.setAttribute('aria-disabled', 'true');
      btn.classList.add('is-loading');
      if (label && S.loading) label.textContent = S.loading + '…';
      var errBox = $('[data-product-error]', form.closest('[data-product-section]') || form);
      if (errBox) { errBox.hidden = true; errBox.textContent = ''; }

      return fetch(R.cartAdd, { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }, body: fd })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, data: j }; }); })
        .then(function (res) {
          if (!res.ok || res.data.status) {
            var msg = res.data.description || res.data.message || S.cartError;
            if (errBox) { errBox.textContent = msg; errBox.hidden = false; }
            announce(msg);
            return;
          }
          var after = T.settings.cartAfterAdd;
          if (T.settings.cartType === 'page' || after === 'page') {
            window.location.href = R.cart;
            return;
          }
          return self.get().then(function (cart) {
            self.setCount(cart.item_count);
            if (res.data.sections) self.render(res.data.sections);
            else return self.refresh();
          }).then(function () {
            announce(S.itemAdded);
            if (useDrawer && after === 'drawer') Drawers.open('cart', btn);
          });
        })
        .catch(function () {
          if (errBox) { errBox.textContent = S.cartError; errBox.hidden = false; }
        })
        .then(function () {
          btn.removeAttribute('aria-disabled');
          btn.classList.remove('is-loading');
          if (label) label.textContent = origLabel;
        });
    }
  };

  /* ======================================================================
     Quantity inputs
     ====================================================================== */
  function clampQty(input) {
    var min = parseInt(input.min, 10) || 1;
    var max = input.max ? parseInt(input.max, 10) : null;
    var step = parseInt(input.step, 10) || 1;
    var val = parseInt(input.value, 10);
    if (isNaN(val)) val = min;
    var isCartLine = input.hasAttribute('data-line');
    if (val <= 0 && isCartLine) return 0;
    if (val < min) { val = min; announce(S.quantityMin); }
    if (max !== null && val > max) { val = max; announce(S.quantityMax); }
    if (step > 1) val = Math.max(min, Math.round((val - min) / step) * step + min);
    return val;
  }

  function onQtyClick(btn) {
    var wrap = btn.closest('[data-qty]');
    var input = $('.qty__input', wrap);
    if (!input) return;
    var step = parseInt(input.step, 10) || 1;
    var cur = parseInt(input.value, 10) || 0;
    var next = btn.hasAttribute('data-qty-plus') ? cur + step : cur - step;
    var min = parseInt(input.min, 10) || 1;
    var isCartLine = input.hasAttribute('data-line');
    if (next < min && !isCartLine) next = min;
    if (next < 0) next = 0;
    if (input.max && next > parseInt(input.max, 10)) { announce(S.quantityMax); return; }
    input.setAttribute('data-prev', input.value);
    input.value = next;
    if (isCartLine) {
      var line = parseInt(input.getAttribute('data-line'), 10);
      Cart.change(line, next, { line: line, kind: btn.hasAttribute('data-qty-plus') ? 'plus' : 'minus' });
    }
  }

  /* ======================================================================
     Announcement bar
     ====================================================================== */
  function initAnnouncement(root) {
    $$('[data-announcement]', root).forEach(function (bar) {
      if (bar.__init) return;
      bar.__init = true;
      var items = $$('[data-announce-item]', bar);
      if (items.length < 2) return;
      var idx = 0, timer = null, paused = false;
      var delay = parseInt(bar.getAttribute('data-delay'), 10) || 5000;
      var auto = bar.getAttribute('data-autoplay') === 'true' && !prefersReducedMotion();
      var track = $('.announce__track', bar);

      function show(i, manual) {
        idx = (i + items.length) % items.length;
        items.forEach(function (el, n) {
          var on = n === idx;
          el.hidden = !on;
          el.classList.toggle('is-active', on);
        });
        if (track) track.setAttribute('aria-live', auto && !manual ? 'off' : 'polite');
      }
      function start() { if (auto && !timer && !paused) timer = setInterval(function () { show(idx + 1); }, delay); }
      function stop() { clearInterval(timer); timer = null; }

      var prev = $('[data-announce-prev]', bar), next = $('[data-announce-next]', bar);
      if (prev) prev.addEventListener('click', function () { show(idx - 1, true); });
      if (next) next.addEventListener('click', function () { show(idx + 1, true); });
      bar.addEventListener('mouseenter', function () { paused = true; stop(); });
      bar.addEventListener('mouseleave', function () { paused = false; start(); });
      bar.addEventListener('focusin', function () { paused = true; stop(); });
      bar.addEventListener('focusout', function () { paused = false; start(); });
      bar.__stop = stop;
      bar.__show = function (el) { var n = items.indexOf(el); if (n > -1) { auto = false; stop(); show(n, true); } };
      start();
    });
  }

  /* ======================================================================
     Header nav dropdowns
     ====================================================================== */
  function closeNav(except) {
    $$('.nav__item.is-open').forEach(function (item) {
      if (item === except) return;
      item.classList.remove('is-open');
      var t = $('.nav__toggle', item);
      if (t) t.setAttribute('aria-expanded', 'false');
    });
  }

  /* ======================================================================
     Footer accordion (accordion on mobile, always open on desktop)
     ====================================================================== */
  function initFooter(root) {
    var list = $$('details[data-footer-acc]', root);
    if (!list.length) return;
    var q = mq('(min-width: 750px)');
    function apply() { list.forEach(function (d) { d.open = q.matches; }); }
    apply();
    if (!root.__footerBound) {
      root.__footerBound = true;
      if (q.addEventListener) q.addEventListener('change', apply); else if (q.addListener) q.addListener(apply);
      list.forEach(function (d) {
        var s = $('summary', d);
        if (s) s.addEventListener('click', function (e) { if (q.matches) e.preventDefault(); });
      });
    }
  }

  /* ======================================================================
     Product gallery + lightbox
     ====================================================================== */
  function initGallery(root) {
    $$('[data-gallery]', root).forEach(function (g) {
      if (g.__init) return;
      g.__init = true;
      var slides = $$('[data-slide]', g);
      var thumbs = $$('[data-thumb]', g);
      var viewport = $('[data-gallery-viewport]', g);
      var status = $('[data-gallery-status]', g);
      var zoomBtn = $('[data-gallery-zoom]', g);
      var sectionEl = g.closest('[data-product-section]') || document;
      var lightbox = $('[data-lightbox]', sectionEl);
      var idx = 0;
      if (!slides.length) return;

      function updateZoom() {
        if (!zoomBtn) return;
        zoomBtn.hidden = !$('img', slides[idx]);
      }
      function go(i, silent) {
        idx = (i + slides.length) % slides.length;
        slides.forEach(function (s, n) {
          var on = n === idx;
          s.classList.toggle('is-active', on);
          s.hidden = !on;
          if (!on) { $$('video', s).forEach(function (v) { try { v.pause(); } catch (e) { /* noop */ } }); }
        });
        thumbs.forEach(function (t, n) {
          t.classList.toggle('is-active', n === idx);
          t.setAttribute('aria-current', n === idx ? 'true' : 'false');
        });
        if (status && !silent) status.textContent = (idx + 1) + ' / ' + slides.length;
        updateZoom();
      }
      g.__goToMedia = function (mediaId) {
        for (var n = 0; n < slides.length; n++) {
          if (String(slides[n].getAttribute('data-slide')) === String(mediaId)) { go(n, true); return; }
        }
      };

      thumbs.forEach(function (t, n) { t.addEventListener('click', function () { go(n); }); });
      var prev = $('[data-gallery-prev]', g), next = $('[data-gallery-next]', g);
      if (prev) prev.addEventListener('click', function () { go(idx - 1); });
      if (next) next.addEventListener('click', function () { go(idx + 1); });

      if (viewport) {
        var x0 = null, y0 = null;
        viewport.addEventListener('touchstart', function (e) { x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; }, { passive: true });
        viewport.addEventListener('touchend', function (e) {
          if (x0 === null) return;
          var dx = e.changedTouches[0].clientX - x0, dy = e.changedTouches[0].clientY - y0;
          x0 = y0 = null;
          if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) go(idx + (dx < 0 ? 1 : -1));
        }, { passive: true });
        viewport.setAttribute('tabindex', '0');
        viewport.addEventListener('keydown', function (e) {
          if (e.key === 'ArrowRight') { e.preventDefault(); go(idx + 1); }
          else if (e.key === 'ArrowLeft') { e.preventDefault(); go(idx - 1); }
        });
      }

      if (zoomBtn && lightbox && typeof lightbox.showModal === 'function') {
        var body = $('[data-lightbox-body]', lightbox);
        zoomBtn.addEventListener('click', function () {
          var img = $('img', slides[idx]);
          if (!img || !body) return;
          var big = document.createElement('img');
          var srcset = img.getAttribute('srcset') || '';
          var parts = srcset.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
          big.src = parts.length ? parts[parts.length - 1].split(' ')[0] : img.currentSrc || img.src;
          big.alt = img.alt || '';
          body.textContent = '';
          body.appendChild(big);
          lightbox.showModal();
        });
        var closeBtn = $('[data-lightbox-close]', lightbox);
        if (closeBtn) closeBtn.addEventListener('click', function () { lightbox.close(); });
        lightbox.addEventListener('click', function (e) { if (e.target === lightbox) lightbox.close(); });
      } else if (zoomBtn) {
        zoomBtn.hidden = true;
      }
      go(0, true);
    });
  }

  /* ======================================================================
     Product tabs (tabs on desktop, accordion on mobile)
     ====================================================================== */
  function initTabs(root) {
    $$('[data-tabs]', root).forEach(function (wrap) {
      if (wrap.__init) return;
      wrap.__init = true;
      var bar = $('[data-tab-bar]', wrap);
      var heads = $$('[data-tab-head]', wrap);
      if (!heads.length) return;
      var panels = heads.map(function (h) { return document.getElementById(h.getAttribute('aria-controls')); });
      var tabs = [];
      var current = 0;
      var q = mq('(min-width: 750px)');

      // build desktop tab bar from the accordion heads
      heads.forEach(function (h, n) {
        var tab = document.createElement('button');
        tab.type = 'button';
        tab.id = 'Tab-' + h.getAttribute('data-tab-head');
        tab.setAttribute('role', 'tab');
        tab.textContent = (h.querySelector('span') || h).textContent.trim();
        tab.addEventListener('click', function () { select(n); });
        tab.addEventListener('keydown', function (e) {
          var k = e.key, to = null;
          if (k === 'ArrowRight') to = (n + 1) % tabs.length;
          else if (k === 'ArrowLeft') to = (n - 1 + tabs.length) % tabs.length;
          else if (k === 'Home') to = 0;
          else if (k === 'End') to = tabs.length - 1;
          if (to !== null) { e.preventDefault(); select(to); tabs[to].focus(); }
        });
        tabs.push(tab);
        if (bar) bar.appendChild(tab);
        h.addEventListener('click', function () {
          if (q.matches) return;
          var open = h.getAttribute('aria-expanded') === 'true';
          h.setAttribute('aria-expanded', open ? 'false' : 'true');
          panels[n].hidden = open;
        });
      });

      function select(n) {
        current = n;
        tabs.forEach(function (t, i) {
          var on = i === n;
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.setAttribute('tabindex', on ? '0' : '-1');
          if (panels[i]) panels[i].hidden = !on;
        });
      }

      function mode() {
        var desktop = q.matches;
        wrap.classList.toggle('is-tabs', desktop);
        wrap.classList.toggle('is-accordion', !desktop);
        heads.forEach(function (h, n) {
          var heading = h.closest('.ptab__heading');
          if (heading) heading.hidden = desktop;
          var p = panels[n];
          if (!p) return;
          if (desktop) {
            p.setAttribute('role', 'tabpanel');
            p.setAttribute('aria-labelledby', tabs[n].id);
          } else {
            p.setAttribute('role', 'region');
            p.setAttribute('aria-labelledby', h.id);
          }
        });
        if (bar) bar.hidden = !desktop;
        if (desktop) {
          select(current);
        } else {
          heads.forEach(function (h, n) {
            var open = n === 0;
            h.setAttribute('aria-expanded', open ? 'true' : 'false');
            if (panels[n]) panels[n].hidden = !open;
          });
        }
      }
      mode();
      if (q.addEventListener) q.addEventListener('change', mode); else if (q.addListener) q.addListener(mode);
    });
  }

  /* ======================================================================
     Variant picker
     ====================================================================== */
  function initProduct(root) {
    $$('[data-product-section]', root).forEach(function (sec) {
      if (sec.__init) return;
      sec.__init = true;
      var jsonEl = $('[data-product-json]', sec);
      var product = null;
      try { product = jsonEl ? JSON.parse(jsonEl.textContent) : null; } catch (e) { product = null; }
      if (!product) return;
      var form = $('[data-product-form]', sec);
      var abort = null;

      function selectedOptions() {
        var picker = $('[data-variant-picker]', sec);
        if (!picker) return null;
        var vals = [];
        $$('[data-option-index]', picker).forEach(function (el) {
          var i = parseInt(el.getAttribute('data-option-index'), 10);
          if (el.type === 'radio') { if (el.checked) vals[i] = el.value; }
          else vals[i] = el.value;
        });
        return vals;
      }

      function findVariant(vals) {
        for (var i = 0; i < product.variants.length; i++) {
          var v = product.variants[i], ok = true;
          for (var j = 0; j < vals.length; j++) { if (v.options[j] !== vals[j]) { ok = false; break; } }
          if (ok) return v;
        }
        return null;
      }

      function setUnavailable() {
        var btn = $('[data-add-button]', sec);
        if (btn) {
          btn.disabled = true;
          var lab = $('[data-add-label]', btn);
          if (lab) lab.textContent = S.unavailable;
          $$('.pbuy__sep, .pbuy__price', btn).forEach(function (e) { e.hidden = true; });
        }
        var dyn = $('.shopify-payment-button', sec);
        if (dyn) dyn.hidden = true;
        announce(S.unavailable);
      }

      function onChange() {
        var vals = selectedOptions();
        if (!vals) return;
        var variant = findVariant(vals);
        if (!variant) { setUnavailable(); return; }
        var idInput = $('[data-variant-id]', sec);
        if (idInput) idInput.value = variant.id;
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());

        var gallery = $('[data-gallery]', sec);
        if (gallery && gallery.__goToMedia && variant.featured_media) gallery.__goToMedia(variant.featured_media.id);

        // keep quantity rules in sync is handled server side on reload; here refresh price + buy buttons
        if (abort) abort.abort();
        abort = typeof AbortController !== 'undefined' ? new AbortController() : null;
        var sectionId = sec.getAttribute('data-section-id');
        var fetchUrl = (sec.getAttribute('data-product-url') || window.location.pathname) + '?variant=' + variant.id + '&section_id=' + encodeURIComponent(sectionId);
        fetch(fetchUrl, abort ? { signal: abort.signal } : {})
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var doc = parseHTML(html);
            ['[data-price-region]', '[data-buy-region]'].forEach(function (sel) {
              var cur = $(sel, sec), nxt = $(sel, doc);
              if (cur && nxt) cur.innerHTML = nxt.innerHTML;
            });
            if (window.Shopify && window.Shopify.PaymentButton && window.Shopify.PaymentButton.init) {
              try { window.Shopify.PaymentButton.init(); } catch (e) { /* noop */ }
            }
            var nextQty = $('.pinfo__qty .qty__input', doc), curQty = $('.pinfo__qty .qty__input', sec);
            if (nextQty && curQty) {
              curQty.min = nextQty.min; curQty.step = nextQty.step;
              if (nextQty.max) curQty.max = nextQty.max; else curQty.removeAttribute('max');
            }
          })
          .catch(function (err) { if (err && err.name === 'AbortError') return; });
      }

      var picker = $('[data-variant-picker]', sec);
      if (picker) picker.addEventListener('change', onChange);
      if (form) form.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' && e.target.matches('.qty__input')) { e.preventDefault(); }
      });
    });
  }

  /* ======================================================================
     Recommendations (lazy)
     ====================================================================== */
  function initRecommendations(root) {
    $$('[data-recommendations][data-url]', root).forEach(function (el) {
      if (el.__init || el.getAttribute('data-loaded')) return;
      el.__init = true;
      function load() {
        fetch(el.getAttribute('data-url'))
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var doc = parseHTML(html);
            var nxt = $('[data-recommendations]', doc);
            if (nxt) {
              nxt.setAttribute('data-loaded', 'true');
              el.replaceWith(nxt);
            }
          })
          .catch(function () { /* leave empty */ });
      }
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          if (entries[0].isIntersecting) { io.disconnect(); load(); }
        }, { rootMargin: '400px' });
        io.observe(el);
      } else {
        load();
      }
    });
  }

  /* ======================================================================
     Collection: AJAX filters / sort / pagination, price slider, view toggle
     ====================================================================== */
  var Collection = {
    pending: null,

    section: function () { return $('[data-collection-section]'); },

    init: function (root) {
      this.restoreView(root);
      $$('[data-price-range]', root).forEach(function (r) { Collection.initPrice(r); });
    },

    restoreView: function (root) {
      var saved = store('moksha-view');
      if (saved !== 'list' && saved !== 'grid') return;
      var grid = $('[data-view]', root || document);
      if (grid) this.setView(saved);
    },

    setView: function (view) {
      var grid = $('.cprod [data-view]');
      if (!grid) return;
      grid.setAttribute('data-view', view);
      $$('[data-view-btn]').forEach(function (b) {
        var on = b.getAttribute('data-view-btn') === view;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    },

    initPrice: function (wrap) {
      if (wrap.__init) return;
      wrap.__init = true;
      var inputs = $$('.price-range__input', wrap);
      var sliders = $$('[data-range-slider]', wrap);
      if (inputs.length < 2 || sliders.length < 2) return;
      var minIn = inputs[0], maxIn = inputs[1];
      var minS = sliders[0], maxS = sliders[1];
      var top = parseFloat(maxS.max);

      function syncSliders() {
        var a = parseFloat(minIn.value), b = parseFloat(maxIn.value);
        minS.value = isNaN(a) ? 0 : a;
        maxS.value = isNaN(b) ? top : b;
      }
      minS.addEventListener('input', function () {
        if (parseFloat(minS.value) > parseFloat(maxS.value)) minS.value = maxS.value;
        minIn.value = parseFloat(minS.value) > 0 ? minS.value : '';
      });
      maxS.addEventListener('input', function () {
        if (parseFloat(maxS.value) < parseFloat(minS.value)) maxS.value = minS.value;
        maxIn.value = parseFloat(maxS.value) < top ? maxS.value : '';
      });
      var commit = debounce(function () { Collection.apply(); }, 500);
      [minS, maxS].forEach(function (s) { s.addEventListener('change', commit); });
      [minIn, maxIn].forEach(function (i) {
        i.addEventListener('input', syncSliders);
        i.addEventListener('change', function () { syncSliders(); commit(); });
      });
    },

    urlFromForm: function () {
      var form = $('#FiltersForm');
      if (!form) return window.location.href;
      var fd = new FormData(form);
      var params = new URLSearchParams();
      fd.forEach(function (v, k) { if (v !== '' && k !== 'page') params.append(k, v); });
      var base = form.getAttribute('action') || window.location.pathname;
      var qs = params.toString();
      return base + (qs ? '?' + qs : '');
    },

    apply: function () { return this.render(this.urlFromForm(), true); },

    render: function (href, push) {
      var sec = this.section();
      if (!sec) { window.location.href = href; return Promise.resolve(); }
      var sectionId = sec.getAttribute('data-section-id');
      var u = new URL(href, window.location.origin);
      var shown = new URL(u.toString());
      u.searchParams.set('section_id', sectionId);
      var self = this;
      if (this.pending) this.pending.abort();
      this.pending = typeof AbortController !== 'undefined' ? new AbortController() : null;
      sec.setAttribute('aria-busy', 'true');

      // remember which filter groups the shopper opened
      var openStates = $$('.filters__group').map(function (d) { return d.open; });
      var filtersScroll = $('.filters__body');
      var scrollTop = filtersScroll ? filtersScroll.scrollTop : 0;
      var hadFocus = document.activeElement && document.activeElement.id;

      return fetch(u.toString(), this.pending ? { signal: this.pending.signal } : {})
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = parseHTML(html);
          var nextResults = $('[data-results]', doc);
          var curResults = $('[data-results]');
          if (!nextResults || !curResults) { window.location.href = shown.toString(); return; }
          var view = $('.cprod [data-view]') && $('.cprod [data-view]').getAttribute('data-view');
          curResults.innerHTML = nextResults.innerHTML;
          var nextFilters = $('[data-filters]', doc), curFilters = $('[data-filters]');
          if (nextFilters && curFilters) {
            curFilters.innerHTML = nextFilters.innerHTML;
            $$('.filters__group').forEach(function (d, i) { if (openStates[i] !== undefined) d.open = openStates[i]; });
            var nb = $('.filters__body');
            if (nb) nb.scrollTop = scrollTop;
          }
          if (view) self.setView(view);
          $$('[data-price-range]').forEach(function (r) { self.initPrice(r); });
          if (push) window.history.pushState({ moksha: true }, '', shown.toString());
          var count = $('.cprod__count');
          announce((S.filtersUpdated || '') + (count ? '. ' + count.textContent.trim() : ''));
          if (hadFocus) { var f = document.getElementById(hadFocus); if (f) f.focus({ preventScroll: true }); }
        })
        .catch(function (err) { if (err && err.name === 'AbortError') return; window.location.href = shown.toString(); })
        .then(function () { sec.removeAttribute('aria-busy'); });
    }
  };

  /* ======================================================================
     Forms
     ====================================================================== */
  function validateForm(form) {
    var firstBad = null;
    $$('input, select, textarea', form).forEach(function (el) {
      if (el.type === 'hidden' || el.type === 'submit') return;
      var field = el.closest('.field');
      var err = field && $('[data-field-error]', field);
      var bad = el.hasAttribute('required') && !el.validity.valid;
      if (el.type === 'email' && el.value && !el.validity.valid) bad = true;
      el.setAttribute('aria-invalid', bad ? 'true' : 'false');
      if (err) {
        err.hidden = !bad;
        if (bad && err.id) {
          var d = (el.getAttribute('aria-describedby') || '').split(' ').filter(Boolean);
          if (d.indexOf(err.id) === -1) d.push(err.id);
          el.setAttribute('aria-describedby', d.join(' '));
        }
      }
      if (bad && !firstBad) firstBad = el;
    });
    if (firstBad) { firstBad.focus(); announce(firstBad.closest('.field') && $('[data-field-error]', firstBad.closest('.field')) ? $('[data-field-error]', firstBad.closest('.field')).textContent : ''); }
    return !firstBad;
  }

  function copyText(text, btn) {
    function done() { announce(S.linkCopied || ''); if (btn) { btn.classList.add('is-copied'); setTimeout(function () { btn.classList.remove('is-copied'); }, 1500); } }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done, function () { /* ignore */ });
    } else {
      var ta = document.createElement('textarea');
      ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); done(); } catch (e) { /* ignore */ }
      ta.remove();
    }
  }

  /* ======================================================================
     Delegated events
     ====================================================================== */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!(t instanceof Element)) return;

    var opener = t.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      Drawers.open(opener.getAttribute('data-drawer-open'), opener);
      return;
    }
    var cartOpen = t.closest('[data-cart-open]');
    if (cartOpen && Cart.drawerEnabled() && !(e.metaKey || e.ctrlKey || e.shiftKey)) {
      e.preventDefault();
      Drawers.open('cart', cartOpen);
      return;
    }
    if (t.closest('[data-drawer-close]')) { e.preventDefault(); Drawers.close(); return; }
    if (t.matches('[data-overlay]')) { Drawers.close(); return; }

    var qtyBtn = t.closest('[data-qty-minus], [data-qty-plus]');
    if (qtyBtn) { e.preventDefault(); onQtyClick(qtyBtn); return; }

    var rm = t.closest('[data-remove-line]');
    if (rm) {
      e.preventDefault();
      var line = parseInt(rm.getAttribute('data-remove-line'), 10);
      Cart.change(line, 0, { line: line, kind: 'remove' });
      return;
    }

    var toggle = t.closest('.nav__toggle');
    if (toggle) {
      var item = toggle.closest('.nav__item');
      var open = !item.classList.contains('is-open');
      closeNav(item);
      item.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      return;
    }
    if (!t.closest('.nav')) closeNav();

    var fl = t.closest('[data-filter-link]');
    if (fl && Collection.section()) {
      e.preventDefault();
      Collection.render(fl.getAttribute('href'), true);
      return;
    }
    var pg = t.closest('.cprod .pagination a');
    if (pg && Collection.section()) {
      e.preventDefault();
      Collection.render(pg.getAttribute('href'), true).then(function () {
        var top = $('.cprod__toolbar') || Collection.section();
        if (top) top.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
      });
      return;
    }

    var vb = t.closest('[data-view-btn]');
    if (vb) {
      var view = vb.getAttribute('data-view-btn');
      Collection.setView(view);
      store('moksha-view', view);
      return;
    }

    var cp = t.closest('[data-copy-link]');
    if (cp) { e.preventDefault(); copyText(cp.getAttribute('data-copy-link'), cp); return; }

    var del = t.closest('[data-delete-address]');
    if (del) {
      e.preventDefault();
      if (window.confirm && !window.confirm(del.getAttribute('data-confirm') || 'Delete this address?')) return;
      var srcForm = del.closest('details') && $('form', del.closest('details'));
      var token = srcForm && $('input[name="authenticity_token"]', srcForm);
      var f = document.createElement('form');
      f.method = 'post';
      f.action = del.getAttribute('data-delete-address');
      [['_method', 'delete'], ['authenticity_token', token ? token.value : '']].forEach(function (p) {
        var i = document.createElement('input'); i.type = 'hidden'; i.name = p[0]; i.value = p[1]; f.appendChild(i);
      });
      document.body.appendChild(f);
      f.submit();
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      if (Drawers.active) { Drawers.close(); return; }
      var openNav = $('.nav__item.is-open');
      if (openNav) {
        var tg = $('.nav__toggle', openNav);
        closeNav();
        if (tg) tg.focus();
      }
    }
    Drawers.trap(e);
  });

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement)) return;

    if (form.hasAttribute('data-ajax-cart')) {
      if (e.submitter && e.submitter.name === 'checkout') return; /* Buy it now: native Shopify add + redirect to checkout */
      var btn = $('[data-add-button]', form);
      if (!btn || btn.disabled) { e.preventDefault(); return; }
      e.preventDefault();
      if (btn.getAttribute('aria-disabled') === 'true') return;
      Cart.add(form, btn);
      return;
    }
    if (form.hasAttribute('data-filters-form') && Collection.section()) {
      e.preventDefault();
      Collection.apply();
      return;
    }
    if (form.hasAttribute('data-validate')) {
      if (!validateForm(form)) e.preventDefault();
    }
  });

  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!(t instanceof Element)) return;

    if (t.matches('[data-auto-submit]')) { var f = t.closest('form'); if (f) f.submit(); return; }

    if (t.matches('.qty__input')) {
      var val = clampQty(t);
      if (t.hasAttribute('data-line')) {
        var line = parseInt(t.getAttribute('data-line'), 10);
        t.value = val;
        Cart.change(line, val, { line: line, kind: 'input' });
      } else {
        t.value = val;
      }
      return;
    }

    if (t.matches('[data-sort-select]') || (t.closest('[data-filters-form]') && !t.matches('[data-range-slider], .price-range__input'))) {
      if (Collection.section()) Collection.apply();
      return;
    }

    if (t.matches('[data-cart-note]')) {
      fetch((R.cart || '/cart') + '/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: t.value }) });
    }
  });

  document.addEventListener('focusin', function (e) {
    var t = e.target;
    if (t instanceof Element && t.matches('.qty__input')) t.setAttribute('data-prev', t.value);
  });

  document.addEventListener('input', function (e) {
    var t = e.target;
    if (!(t instanceof Element)) return;
    if (t.matches('[data-validate] input, [data-validate] textarea, [data-validate] select')) {
      var field = t.closest('.field'), err = field && $('[data-field-error]', field);
      if (err && t.validity.valid) { err.hidden = true; t.setAttribute('aria-invalid', 'false'); }
    }
  });

  window.addEventListener('popstate', function () {
    if (Collection.section()) Collection.render(window.location.href, false);
  });

  window.addEventListener('pageshow', function (e) {
    if (e.persisted && R.cartJs) Cart.get().then(function (c) { Cart.setCount(c.item_count); }).catch(function () { /* noop */ });
  });

  /* ======================================================================
     Product rating: Judge.me first, manual fallback second, otherwise hidden.
     Judge.me injects its preview badge into [data-rating-judgeme]; the manual
     rating is only revealed when Judge.me reports no reviews (or never loads).
     ====================================================================== */
  function initRating(root) {
    $$('[data-rating]', root).forEach(function (el) {
      if (el.__rating) return;
      el.__rating = true;
      var jm = $('[data-rating-judgeme]', el);
      var manual = $('[data-rating-manual]', el);
      var block = el.closest('.pblock');
      var start = Date.now();
      function reviewCount() {
        var b = jm && $('.jdgm-prev-badge', jm);
        if (!b) return -1;
        var n = parseInt(b.getAttribute('data-number-of-reviews'), 10);
        return isNaN(n) ? -1 : n;
      }
      function setHidden(node, v) { if (node && node.hidden !== v) node.hidden = v; }
      function show(mode) {
        setHidden(jm, mode !== 'jm');
        setHidden(manual, mode !== 'manual');
        setHidden(block, mode === 'none');
        if (el.getAttribute('data-resolved') !== mode) el.setAttribute('data-resolved', mode);
      }
      function resolve() {
        if (!jm) { show(manual ? 'manual' : 'none'); return; }
        var n = reviewCount();
        var waited = Date.now() - start > 2500;
        if (n > 0) show('jm');
        else if (n === 0 || waited) show(manual ? 'manual' : 'none');
        else if (el.getAttribute('data-resolved') !== 'pending') el.setAttribute('data-resolved', 'pending');
      }
      resolve();
      if (jm) {
        if (window.MutationObserver) new MutationObserver(resolve).observe(jm, { childList: true, subtree: true, attributes: true });
        setTimeout(resolve, 2600);
        window.addEventListener('load', function () { setTimeout(resolve, 600); });
      }
      el.addEventListener('click', function () {
        if (el.getAttribute('data-resolved') !== 'jm') return;
        var target = document.getElementById('judgeme_product_reviews') || $('.jdgm-review-widget') || $('.jdgm-rev-widg');
        if (target && target.scrollIntoView) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  /* ======================================================================
     Init (+ theme editor support)
     ====================================================================== */
  function initAll(root) {
    root = root || document;
    Drawers.init(root);
    initAnnouncement(root);
    initFooter(root);
    initGallery(root);
    initTabs(root);
    initProduct(root);
    initRating(root);
    initRecommendations(root);
    Collection.init(root);
    var live = $('[data-form-message]', root);
    if (live && root === document) live.focus({ preventScroll: false });
  }

  function ready(fn) {
    if (document.readyState !== 'loading') fn(); else document.addEventListener('DOMContentLoaded', fn);
  }
  ready(function () {
    initAll(document);
    var q = mq('(min-width: 990px)');
    var onBp = function () { Drawers.syncFilters(); };
    if (q.addEventListener) q.addEventListener('change', onBp); else if (q.addListener) q.addListener(onBp);
  });

  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    $$('[data-announcement]', e.target).forEach(function (b) { if (b.__stop) b.__stop(); });
  });
  document.addEventListener('shopify:block:select', function (e) {
    var bar = e.target.closest && e.target.closest('[data-announcement]');
    if (bar && bar.__show) bar.__show(e.target);
  });
})();
